import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LivingPerspective } from "@/components/perspective/LivingPerspective";
import { PropertyCard } from "@/components/PropertyCard";
import { PropertySearch } from "@/components/PropertySearch";
import { useApp } from "@/context/AppContext";
import { IMAGES } from "@/data/seed";

const METHOD = [
  { t: "Understand your life", d: "Work, family, schools, community and future needs — before any project." },
  { t: "Map the everyday", d: "Real travel corridors, peak-hour reality and connectivity, not just distance." },
  { t: "Evaluate the fit", d: "Space, density, amenities and long-term suitability, measured against you." },
  { t: "Curate, don't overwhelm", d: "A short set of homes that genuinely fit — higher conviction, fewer options." },
];

export default function Living() {
  const { data } = useApp();
  const properties = data.properties.filter((p) => p.published && p.category.includes("living")).slice(0, 6);

  return (
    <div>
      <section className="relative min-h-[80vh] overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.living} alt="Living" className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,15,12,0.94), rgba(5,15,12,0.7))" }} />
        </div>
        <div className="hero-copy container-arch relative flex min-h-[80vh] flex-col justify-center pt-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <div className="overline !text-[#9ED3B8]">Archedia Living · For the life you want to live</div>
            <h1 className="mt-4 font-serif text-4xl font-light leading-[1.1] text-white sm:text-5xl lg:text-6xl">A Home Should Fit Your Life — Not Just Your Budget.</h1>
            <Link to="/request?purpose=Living" className="btn-gold mt-8 !bg-[var(--accent-gold)] !text-[#071711]">Find My Home <ArrowUpRight className="h-4 w-4" /></Link>
          </motion.div>
        </div>
      </section>

      <section className="container-arch relative z-20 -mt-8" data-testid="vertical-search">
        <div className="arch-card p-3 sm:p-4"><PropertySearch /></div>
      </section>

      <section className="container-arch py-20 lg:py-28">
        <Reveal><div className="overline">The Living Perspective</div>
          <h2 className="mt-3 max-w-2xl font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Your life at the centre — everything else evaluated around it.</h2>
        </Reveal>
        <div className="mt-14"><LivingPerspective /></div>
      </section>

      <section className="border-y border-[var(--border-subtle)] bg-[var(--bg-card)] py-20 lg:py-28">
        <div className="container-arch">
          <Reveal><h2 className="font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Our Living methodology</h2></Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {METHOD.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.08}>
                <div className="arch-card h-full p-6">
                  <div className="font-mono text-sm text-[var(--accent-gold)]">0{i + 1}</div>
                  <h4 className="mt-3 font-serif text-xl text-[var(--text-primary)]">{m.t}</h4>
                  <p className="mt-2 text-sm text-[var(--text-muted)]">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-arch py-20 lg:py-28">
        <Reveal><div className="overline">Curated for living</div><h2 className="mt-3 font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Homes worth a closer perspective</h2></Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
